import { useAuthActions } from '@convex-dev/auth/react';
import {
  Laptop,
  LogOut,
  Moon,
  Sun,
  User as UserIcon,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import React from 'react';
import { Link } from 'wouter';

import { useIsMobile } from '@/hooks/use-mobile';
import { useSidebar } from '../ui/sidebar';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface UserDropdownProps {
  variant?: 'sidebar' | 'topbar';
  userEmail?: string;
  userName?: string;
}

export const UserDropdown = ({
  variant = 'sidebar',
  userEmail = 'user@example.com', // Default value until we connect to auth
  userName = 'User', // Default value until we connect to auth
}: UserDropdownProps) => {
  const { theme, setTheme } = useTheme();
  const { signOut } = useAuthActions();
  const { setOpenMobile } = useSidebar();
  const isMobile = useIsMobile();

  const handleSettingsClick = () => {
    // Close sidebar on mobile when navigating to settings
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'topbar' ? (
          <button className="flex items-center justify-center h-8 w-8 rounded-full bg-muted">
            <UserIcon className="h-4 w-4" />
          </button>
        ) : (
          <button className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-blue-100 hover:text-blue-900 dark:hover:bg-blue-900 dark:hover:text-blue-100 transition-colors">
            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-sidebar-accent/20">
              <UserIcon className="h-4 w-4" />
            </div>
            <div className="flex flex-col items-start text-left">
              <span className="text-sm font-medium truncate">{userName}</span>
              <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                {userEmail}
              </span>
            </div>
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === 'topbar' ? 'end' : 'start'}
        side={variant === 'sidebar' ? 'right' : 'bottom'}
        className="w-56"
      >
        <div className="p-2">
          <p className="text-sm font-medium">{userName}</p>
          <p className="text-xs text-muted-foreground">{userEmail}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to="/profile"
            className="cursor-pointer flex w-full items-center"
            onClick={handleSettingsClick}
          >
            <UserIcon className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => setTheme('light')}
          className="cursor-pointer"
        >
          <Sun className="mr-2 h-4 w-4" />
          <span>Light</span>
          {theme === 'light' && <span className="ml-auto">✓</span>}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme('dark')}
          className="cursor-pointer"
        >
          <Moon className="mr-2 h-4 w-4" />
          <span>Dark</span>
          {theme === 'dark' && <span className="ml-auto">✓</span>}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme('system')}
          className="cursor-pointer"
        >
          <Laptop className="mr-2 h-4 w-4" />
          <span>System</span>
          {theme === 'system' && <span className="ml-auto">✓</span>}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="p-2">
          <button
            className="flex w-full items-center rounded-md px-2 py-1 bg-slate-200 dark:bg-slate-800 text-dark dark:text-light"
            onClick={() => void signOut()}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
